import { entries, values } from "lodash";
import { OriginatingTransactionPortfolioItem } from "../../../../../../types/amr-pipeline/models/OriginatingTransactionPortfolioItem";
import { FitchRating, MoodysRating, parseRating, SnpRating } from "../../../../../../types/models/rating/AgencyRatings";
import { apiUtils, arrayUtils, numericUtils } from "../../../../../../utils";
import { PortfolioStatisticsChart } from "./PortfolioStatisticsChart";

interface Props {
    portfolio: OriginatingTransactionPortfolioItem[];
    children: (content: React.ReactNode) => React.ReactNode;
}

export function PortolioStatisticsCharts({ portfolio, children }: Props) {
    const totalTargetPar = arrayUtils.sum(portfolio, p => numericUtils.numberOrDefault(p.targetPar, 0))

    const moodys: Record<MoodysRating, number> = apiUtils.normalize(values(MoodysRating), r => r as MoodysRating, () => 0);
    const snp: Record<SnpRating, number> = apiUtils.normalize(values(SnpRating), r => r as SnpRating, () => 0);
    const fitch: Record<FitchRating, number> = apiUtils.normalize(values(FitchRating), r => r as FitchRating, () => 0);

    const snpAggregated = { ...snp };
    const moodysAggregated = { ...moodys };
    const fitchAggreagated = { ...fitch };

    let
        hasSnpRatings = false,
        hasFitchRatings = false,
        hasMoodysRatings = false;

    portfolio.forEach(p => {
        const snp = parseRating(SnpRating, p.snpTrancheRating?.trim() ?? '', SnpRating.NR);
        const moodys = parseRating(MoodysRating, p.moodysTrancheRating?.trim() ?? '', MoodysRating.NR);
        const fitch = parseRating(FitchRating, p.fitchTrancheRating?.trim() ?? '', FitchRating.NR);

        const targetPar = numericUtils.numberOrDefault(p.targetPar, 0)

        hasSnpRatings = (snp !== SnpRating.NR && targetPar > 0) || hasSnpRatings;
        hasFitchRatings = (fitch !== FitchRating.NR && targetPar > 0) || hasFitchRatings;
        hasMoodysRatings = (moodys !== MoodysRating.NR && targetPar > 0) || hasMoodysRatings;

        snpAggregated[snp] += targetPar;
        moodysAggregated[moodys] += targetPar;
        fitchAggreagated[fitch] += targetPar;
    })

    const calculatePercentage = <T extends MoodysRating | SnpRating | FitchRating>(
        aggregated: Record<T, number>, result: Record<T, number>) =>
        entries(aggregated).forEach(([rating, amount]) => {
            const value = numericUtils.round(Number(amount) / totalTargetPar * 100, 2);
            if (value) {
                result[rating as T] = value;
            } else {
                delete result[rating as T];
            }
        })

    calculatePercentage(snpAggregated, snp);
    calculatePercentage(moodysAggregated, moodys);
    calculatePercentage(fitchAggreagated, fitch);

    return (
        <>
            {(hasSnpRatings || hasFitchRatings || hasMoodysRatings) &&
                children(
                    <div className="dashboard-row flex-row" >
                        {hasMoodysRatings && <PortfolioStatisticsChart chartName="Moody's" targetParByRating={moodys} />}
                        {hasSnpRatings && <PortfolioStatisticsChart chartName="S&P" targetParByRating={snp} />}
                        {hasFitchRatings && <PortfolioStatisticsChart chartName="Fitch" targetParByRating={fitch} />}
                    </div >
                )
            }
        </>
    );
}